import type { ActionReturn } from 'svelte/action';

type Options = {
  enabled?: boolean;
};

export const focusTrap = (node: HTMLElement, options?: Options): ActionReturn => {
  let enabled = options?.enabled !== false;

  const onKeyDown = (e: KeyboardEvent) => {
    if (enabled && e.key === 'Tab') {
      const selectorTabbable = [
        'a[href]',
        'area[href]',
        "input:not([disabled]):not([tabindex='-1'])",
        "button:not([disabled]):not([tabindex='-1'])",
        "select:not([disabled]):not([tabindex='-1'])",
        "textarea:not([disabled]):not([tabindex='-1'])",
        "iframe:not([tabindex='-1'])",
        'object',
        'embed',
        "*[tabindex]:not([tabindex='-1']):not([disabled])",
        '*[contenteditable=true]'
      ];

      const tabbable = Array.from(node.querySelectorAll(selectorTabbable.join(', ')));

      if (document.activeElement) {
        let index = tabbable.indexOf(document.activeElement);

        let finishedFocus = false;

        // this loop is used to prevent trying to focus
        // elements that are hidden by css.
        // E.g.: display: none
        let tries = 0;
        for (; finishedFocus === false; tries++) {
          // Prevent infinite loop
          if (tries > tabbable.length) return;

          index += e.shiftKey ? -1 : 1;
          index %= tabbable.length;

          const nextFocusElement = tabbable.at(index % tabbable.length);

          if (nextFocusElement && nextFocusElement instanceof HTMLElement) {
            nextFocusElement.focus();
            if (nextFocusElement === document.activeElement) {
              finishedFocus = true;
            }
          }
        }
      }
      e.preventDefault();
    }
  };

  node.addEventListener('keydown', onKeyDown);

  return {
    update(newOptions?: Options) {
      enabled = newOptions?.enabled !== false;
    },
    destroy() {
      node.removeEventListener('keydown', onKeyDown);
    }
  };
};
